// Project utility functions
import { enforceProjectCreationLimit } from './project-limits';
import type { TaskItem } from '../types/topic';
import { logger } from '../utils/logger';

export const handleNewProjectClick = async (user, navigate) => {
  if (!user) {
    console.log('No user found.');
    return;
  }

  try {
    const limitCheck = await enforceProjectCreationLimit(user.id);
    if (!limitCheck.allowed) {
      console.log('Navigating to subscription due to project limit.');
      navigate('/subscription', {
        state: {
          reason: 'project_limit',
          message:
            "You've reached the maximum number of projects for the free plan.\n Upgrade to Pro to create unlimited projects.",
        },
        replace: true,
      });
      return;
    }
    return true; // Allow project creation
  } catch (error) {
    console.error('Error checking project limits:', error);
  }
};

/**
 * Utility to determine task's deadline status
 * @param task Task item with optional deadline
 * @returns Object with deadline status details
 */
export const getTaskDeadlineStatus = (task: TaskItem) => {
  if (!task.deadline) return { status: 'no_deadline', daysUntil: null };

  const deadline = new Date(task.deadline);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for accurate comparison

  const timeDiff = deadline.getTime() - today.getTime();
  const daysUntil = Math.ceil(timeDiff / (1000 * 3600 * 24));

  if (daysUntil < 0) return { status: 'overdue', daysUntil };
  if (daysUntil === 0) return { status: 'due_today', daysUntil: 0 };
  if (daysUntil <= 3) return { status: 'due_soon', daysUntil };

  return { status: 'upcoming', daysUntil };
};

/**
 * Normalizes a date for use as a task deadline to avoid timezone inconsistencies.
 * Preserves the local date (year, month, day) but standardizes the time component.
 *
 * @param date - Date to normalize (can be a Date object, ISO string, or other valid input)
 * @returns An ISO string with the normalized date, or null if invalid input
 */
export const normalizeTaskDeadline = (date: Date | string | null | undefined): string | null => {
  // Handle null or undefined
  if (date == null) return null;

  try {
    let inputDate: Date;

    // Convert to a Date object if it's not already
    if (date instanceof Date) {
      // Check if it's a valid date
      if (isNaN(date.getTime())) {
        logger.error('Invalid Date object provided to normalizeTaskDeadline');
        return null;
      }
      inputDate = date; // Use the input date (we'll create a new date object below)
    } else {
      // Handle string or other values by parsing them
      inputDate = new Date(date);

      // Verify the parsed date is valid
      if (isNaN(inputDate.getTime())) {
        logger.error('Failed to parse date in normalizeTaskDeadline', { input: String(date) });
        return null;
      }
    }

    // Get the local year, month, and day values from the input date
    const year = inputDate.getFullYear();
    const month = inputDate.getMonth();
    const day = inputDate.getDate();

    // Create a new date using the local year, month, and day, but with a standardized time (12:00:00)
    // This ensures we preserve the correct day in the user's local timezone
    const normalizedDate = new Date(year, month, day, 12, 0, 0, 0);

    // Log the normalization for debugging
    logger.component('Normalized task deadline', {
      inputDate: inputDate.toISOString(),
      normalizedDate: normalizedDate.toISOString(),
      localYear: year,
      localMonth: month,
      localDay: day,
    });

    // Return the full ISO string
    return normalizedDate.toISOString();
  } catch (error) {
    logger.error('Error in normalizeTaskDeadline', {
      error: error instanceof Error ? error.message : String(error),
      input: String(date),
    });
    return null;
  }
};

/**
 * Format deadline for human-readable display - unified function across the app
 * @param deadline ISO date string
 * @returns Formatted deadline information including text, styling, and status
 */
export const formatDeadline = (deadline?: string) => {
  if (!deadline) return null;

  // Create date objects for comparison
  const date = new Date(deadline);
  const today = new Date();

  // Convert both dates to their date-only representations to ensure accurate comparison
  // This normalizes both dates to midnight (00:00:00) for the current day
  const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // Key states using normalized dates for comparison
  const isToday = normalizedDate.getTime() === normalizedToday.getTime();
  const isOverdue = normalizedDate < normalizedToday;
  const isPastDue = isOverdue && !isToday;

  // For future dates, calculate how many days in the future using normalized dates
  const daysUntil = isOverdue
    ? 0
    : Math.round((normalizedDate.getTime() - normalizedToday.getTime()) / (1000 * 3600 * 24));
  const isUpcoming = daysUntil > 0 && daysUntil <= 7; // Within a week

  // Calculate "late by" text for overdue tasks
  let lateByText = '';

  if (isPastDue) {
    // Calculate days between dates for overdue tasks using normalized dates
    const timeDiff = Math.abs(normalizedToday.getTime() - normalizedDate.getTime());
    const daysDiff = Math.round(timeDiff / (1000 * 3600 * 24));

    // Special case - if the difference is 0 days, it's actually due today
    if (daysDiff === 0) {
      lateByText = 'Due today';
    } else if (daysDiff < 7) {
      // Less than a week late
      lateByText = `${daysDiff} ${daysDiff === 1 ? 'day' : 'days'} late`;
    } else if (daysDiff < 30) {
      // Less than a month late - show in weeks
      const weeks = Math.floor(daysDiff / 7);
      lateByText = `${weeks} ${weeks === 1 ? 'week' : 'weeks'} late`;
    } else if (daysDiff < 365) {
      // Less than a year late - show in months
      const months = Math.floor(daysDiff / 30);
      lateByText = `${months} ${months === 1 ? 'month' : 'months'} late`;
    } else {
      // More than a year late
      const years = Math.floor(daysDiff / 365);
      lateByText = `${years} ${years === 1 ? 'year' : 'years'} late`;
    }
  } else if (isToday) {
    lateByText = 'Due today';
  } else if (isUpcoming) {
    // For upcoming dates within a week
    lateByText = daysUntil === 1 ? 'Due tomorrow' : `Due in ${daysUntil} days`;
  }

  // Prepare the formatted date text
  const formattedText = isToday
    ? 'Due today'
    : daysUntil === 1
      ? 'Due tomorrow'
      : date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
        });

  // Determine the appropriate text color for the deadline
  const color = isToday
    ? 'text-green-500' // Today - green
    : isPastDue
      ? 'text-red-500' // Overdue - red
      : isUpcoming
        ? 'text-blue-500' // Upcoming soon - blue
        : 'text-gray-500'; // Far in future - gray

  return {
    formatted: formattedText,
    isOverdue: isPastDue,
    isToday,
    isUpcoming,
    daysUntil,
    lateByText,
    // For styling purposes
    color,
  };
};
